'use client';

import { useTranslations } from 'next-intl';
import { useParams, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import { Button } from '@/components/Button/Button';
import { Error } from '@/components/Error/Error';
import { GenericError } from '@/components/Error/GenericError/GenericError';
import { Navbar } from '@/components/Navbar/Navbar';
import { BASE_PATH } from '@/constants/constants';
import analytics from '@/lib/analytics/analytics';
import { CONFIG_ORIGIN_PARAM_KEY, CONFIG_ORIGINS } from '@/lib/config-origin/const';
import { getConfigOriginFromSearchParams } from '@/lib/config-origin/getConfigOriginFromSearchParams';
import { type AppErrorCode, ERROR_CODES_FATAL, ERROR_CODES_WHICH_TRIGGER_GENERIC_ERROR_PAGE } from '@/lib/error-handling/error-codes';
import { logError } from '@/lib/error-handling/error-logging';
import { paramsToPersistForConfig, paramsToPersistsForSEAC } from '@/utils/param-persistence/searchParamsToPersistInFlow';

const eventCommon = { action: 'click-cta', componentGroup: 'error-page', componentName: 'new-cars-order' } as const;

const analyticsHandlers = {
	// TAG-139: Click on CTA to go back to the home page
	trackClickModel: () => {
		analytics.trackEvent('ctaevent', { ...eventCommon, label: 'back-homepage' });
	},
	// TAG-140: Click on CTA to contact support
	trackClickContactSupport: () => {
		analytics.trackEvent('ctaevent', { ...eventCommon, label: 'contact-support' });
	},
	// TAG-141: Click on CTA to go back to configurator
	trackClickBackConfigurator: () => {
		analytics.trackEvent('ctaevent', { ...eventCommon, label: 'back-configurator' });
	},
	// TAG-142: Click on CTA to go back to SEAC list page
	trackClickBackSeacOverview: () => {
		analytics.trackEvent('ctaevent', { ...eventCommon, label: 'back-seac-overview' });
	},
	// TAG-143: Click on CTA to go back to SEAC config page
	trackClickBackConfiguratorSeac: () => {
		analytics.trackEvent('ctaevent', { ...eventCommon, label: 'back-configurator-seac' });
	}
};

export default function ErrorPage({ error }: Readonly<{ error: Error & { digest?: string } }>) {
	const t = useTranslations('page-internal-server-error');
	const tGeneric = useTranslations('page-generic-error');
	const tSeac = useTranslations('page-seac-error');
	const { locale, retailer } = useParams();
	const searchParams = useSearchParams();

	const configOrigin = searchParams.has(CONFIG_ORIGIN_PARAM_KEY) ? getConfigOriginFromSearchParams(searchParams) : undefined;

	useEffect(() => {
		// Log the error to an error reporting service
		logError(error as Error);
	}, [error]);

	/**
	 * See ~/docs/errors.mermaid for visual representation
	 * Update if you make any changes to this logic
	 */
	const type: 'generic' | 'generic-fatal' | 'seac' | 'default' = (() => {
		if (ERROR_CODES_FATAL.includes(error.digest as AppErrorCode)) {
			return 'generic-fatal';
		}

		if (ERROR_CODES_WHICH_TRIGGER_GENERIC_ERROR_PAGE.includes(error.digest as AppErrorCode)) {
			if (configOrigin?.key === CONFIG_ORIGINS.seac.key) {
				return 'seac';
			}

			return 'generic';
		}

		return 'default';
	})();

	return (
		<>
			<Navbar logoRedirectUrl="/" />
			{(() => {
				switch (type) {
					case 'generic':
						return (
							<GenericError
								onClickSupport={analyticsHandlers.trackClickContactSupport}
								primaryCtaOverride={
									<Button
										as="a"
										href={`${BASE_PATH}/${locale}/${retailer}/${CONFIG_ORIGINS.configurator.default.redirectPath}?${paramsToPersistForConfig(searchParams)}`}
										icon="arrow-right"
										iconPosition="right"
										onClick={analyticsHandlers.trackClickBackConfigurator}
										variant="tertiary"
									>
										{tSeac('new_config_cta')}
									</Button>
								}
							/>
						);

					case 'generic-fatal':
						return (
							<GenericError
								onClickModel={analyticsHandlers.trackClickModel}
								onClickSupport={analyticsHandlers.trackClickContactSupport}
							/>
						);

					case 'seac':
						return (
							<Error title={tSeac('title')} errorCode={''} description={tSeac('description')}>
								<div className="d-flex d-flex-column gap-6">
									<Button
										as="a"
										href={`${BASE_PATH}/${locale}/${retailer}/${CONFIG_ORIGINS.configurator.default.redirectPath}?${paramsToPersistForConfig(searchParams)}`}
										icon="arrow-right"
										iconPosition="right"
										onClick={analyticsHandlers.trackClickBackConfiguratorSeac}
										variant="tertiary"
									>
										{tSeac('new_config_cta')}
									</Button>
									<Button
										as="a"
										href={`${BASE_PATH}/${locale}/${retailer}/${CONFIG_ORIGINS.seac.default.redirectPath}?${paramsToPersistsForSEAC(searchParams)}`}
										icon="arrow-right"
										iconPosition="right"
										onClick={analyticsHandlers.trackClickBackSeacOverview}
										variant="tertiary"
									>
										{tSeac('seac_cta')}
									</Button>
									<Button
										as="a"
										href={tGeneric('support_cta_link')}
										icon="arrow-right"
										iconPosition="right"
										onClick={analyticsHandlers.trackClickContactSupport}
										variant="tertiary"
									>
										{tGeneric('support_cta')}
									</Button>
								</div>
							</Error>
						);

					case 'default':
					default: {
						return (
							<Error description={t('description')} errorCode={503} title={t('title')}>
								<div className="mt-4 d-flex d-flex-column sm:d-flex-row items-center gap-4 sm:gap-6">
									<Button
										as="a"
										href={t('primary_cta_href')}
										icon="arrow-right"
										iconPosition="right"
										iconSize="1em"
										onClick={analyticsHandlers.trackClickContactSupport}
										target="_blank"
										variant="tertiary"
									>
										{t('primary_cta_label')}
									</Button>
								</div>
							</Error>
						);
					}
				}
			})()}
		</>
	);
}
